import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useProvince } from "@/contexts/ProvinceContext";
import { Calendar, MapPin, Clock, ChevronLeft, ChevronRight } from "lucide-react";

// ─── Helpers ────────────────────────────────────────────────────────────
const SHORT_DAY_LABELS = ["Pzr", "Pzt", "Sal", "Çar", "Per", "Cum", "Cmt"];

function dayKey(d) {
  return d.toISOString().slice(0, 10);
}

export default function TodayEventsTicker() {
  const { selected } = useProvince();
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  // null = show the whole week; otherwise an ISO date "YYYY-MM-DD"
  const [filterDay, setFilterDay] = useState(null);

  useEffect(() => {
    if (!selected?.id) return;
    const today = new Date();
    const todayStr = dayKey(today);
    const weekEnd = new Date();
    weekEnd.setDate(weekEnd.getDate() + 7);
    const weekEndStr = dayKey(weekEnd);
    api.get(`/events?province_id=${selected.id}&date_from=${todayStr}&date_to=${weekEndStr}&limit=50`)
      .then((r) => {
        const now = Date.now();
        const upcoming = (r.data || []).filter((e) => {
          const t = new Date(e.start_at).getTime();
          return !Number.isNaN(t) && t > now - 6 * 60 * 60 * 1000;
        });
        upcoming.sort((a, b) => new Date(a.start_at) - new Date(b.start_at));
        setItems(upcoming);
      })
      .catch(() => {});
  }, [selected?.id]);

  // Build the 7-day calendar strip with counts, starting from today.
  const dayStrip = useMemo(() => {
    const now = new Date();
    const strip = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date();
      d.setDate(now.getDate() + i);
      const key = dayKey(d);
      const count = items.filter((e) => dayKey(new Date(e.start_at)) === key).length;
      strip.push({
        key,
        count,
        isToday: i === 0,
        shortLabel: i === 0 ? "Bugün" : SHORT_DAY_LABELS[d.getDay()],
        dayNum: d.getDate(),
      });
    }
    return strip;
  }, [items]);

  // Filter items by the selected day (or all)
  const visible = useMemo(() => {
    if (!filterDay) return items;
    return items.filter((e) => dayKey(new Date(e.start_at)) === filterDay);
  }, [items, filterDay]);

  useEffect(() => { setIdx(0); }, [filterDay, items.length]);

  useEffect(() => {
    if (visible.length < 2 || paused) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % visible.length), 4500);
    return () => clearInterval(t);
  }, [visible.length, paused]);

  if (!items.length) return null;
  const safeIdx = Math.min(idx, Math.max(0, visible.length - 1));
  const e = visible[safeIdx];
  const start = e ? new Date(e.start_at) : null;
  const todayStr = dayKey(new Date());
  const eventDay = start ? dayKey(start) : null;
  const isToday = eventDay === todayStr;
  const dayLabel = !start ? ""
    : isToday ? "Bugün"
    : start.toLocaleDateString("tr-TR", { weekday: "long", day: "2-digit", month: "short" });

  return (
    <div
      className="w-full bg-gradient-to-r from-[#e11d2a] to-[#b8161f] text-white rounded-lg overflow-hidden"
      data-testid="today-events-ticker"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Top header: label + counter + prev/next */}
      <div className="flex items-center justify-between px-4 py-1 bg-black/15 border-b border-white/10">
        <div className="flex items-center gap-1.5 text-[10px] md:text-[11px] font-semibold uppercase tracking-[0.18em]">
          <Calendar className="w-3 h-3" />
          <span>Bu Hafta {selected?.name}</span>
          <span className="opacity-70">• {items.length} etkinlik</span>
        </div>
        {visible.length > 1 && (
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setIdx((i) => (i - 1 + visible.length) % visible.length)}
              className="p-0.5 hover:bg-white/15 rounded"
              aria-label="prev"
              data-testid="ticker-prev"
            ><ChevronLeft className="w-3.5 h-3.5" /></button>
            <span className="text-[10px] font-bold tabular-nums">{safeIdx + 1}/{visible.length}</span>
            <button
              onClick={() => setIdx((i) => (i + 1) % visible.length)}
              className="p-0.5 hover:bg-white/15 rounded"
              aria-label="next"
              data-testid="ticker-next"
            ><ChevronRight className="w-3.5 h-3.5" /></button>
          </div>
        )}
      </div>

      {/* Day-of-week tab strip */}
      <div
        className="flex items-stretch gap-1 px-2 py-1.5 bg-black/10 overflow-x-auto no-scrollbar"
        data-testid="ticker-day-tabs"
      >
        <button
          type="button"
          onClick={() => setFilterDay(null)}
          data-testid="ticker-day-all"
          className={`shrink-0 px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wide transition-colors ${
            filterDay === null ? "bg-white text-[#e11d2a]" : "bg-white/10 hover:bg-white/20 text-white"
          }`}
        >
          Tümü <span className="opacity-80 ml-0.5">({items.length})</span>
        </button>
        {dayStrip.map((d) => {
          const selected = filterDay === d.key;
          const disabled = d.count === 0;
          return (
            <button
              key={d.key}
              type="button"
              onClick={() => !disabled && setFilterDay(selected ? null : d.key)}
              disabled={disabled}
              data-testid={`ticker-day-${d.key}`}
              className={`shrink-0 flex flex-col items-center justify-center min-w-[46px] px-1 py-0.5 rounded-md transition-colors ${
                selected
                  ? "bg-white text-[#e11d2a]"
                  : disabled
                    ? "bg-white/5 text-white/40 cursor-not-allowed"
                    : "bg-white/10 hover:bg-white/20 text-white"
              }`}
            >
              <span className="text-[9px] font-bold uppercase leading-none">{d.shortLabel}</span>
              <span className="text-[14px] font-black leading-tight tabular-nums">{d.dayNum}</span>
              {d.count > 0 ? (
                <span className={`text-[9px] font-bold leading-none ${selected ? "text-[#e11d2a]" : "text-yellow-300"}`}>
                  {d.count} etk.
                </span>
              ) : (
                <span className="text-[9px] leading-none opacity-50">—</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Event card */}
      {e ? (
        <Link to={`/etkinlikler/${e.id}`} className="block px-4 md:px-6 py-3 md:py-4">
          <div key={e.id} className="today-event-slide">
            <div className="flex items-center gap-2 mb-1">
              <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${isToday ? "bg-yellow-300 text-black" : "bg-white/20 text-white"}`}>
                {dayLabel}
              </span>
            </div>
            <div className="text-base md:text-xl font-black leading-tight line-clamp-2">{e.title}</div>
            <div className="flex items-center gap-4 mt-1.5 text-xs md:text-sm text-white/90 flex-wrap">
              <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {start.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}</span>
              {e.venue && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {e.venue}</span>}
              {e.event_type && <span className="px-1.5 py-0.5 bg-white/15 rounded text-[10px] uppercase tracking-wider">{e.event_type}</span>}
            </div>
          </div>
        </Link>
      ) : (
        <div className="px-4 py-6 text-center text-sm text-white/80">
          Bu güne ait etkinlik yok.
        </div>
      )}
    </div>
  );
}
